// src/components/media360/domain/naming.js
// Convención de nombres (sin backend): deducimos kind/mode/loop/audio y clave de agrupación.

import { detectKindFromName, stripExt } from "./fileTypes";

/**
 * Deducción robusta del modo estéreo a partir del nombre del objeto.
 * Compatibilidad con la convención existente:
 * - "__tb." => 3d-tb
 * - "__lr." => 3d-sbs
 * También soporta "__tb__" / "__lr__"
 */
export function inferStereoModeFromName(name = "") {
  const n = String(name).toLowerCase();
  if (n.includes("__tb.") || n.includes("__tb__")) return "3d-tb";
  if (n.includes("__lr.") || n.includes("__lr__")) return "3d-sbs";
  return "mono";
}

/**
 * Loop infinito:
 * - Nuevo: "__loop"
 * - Compat real visto en bucket: "-loop"
 */
export function inferLoopFromName(name = "") {
  const n = String(name).toLowerCase();
  return n.includes("__loop") || n.includes("-loop");
}

/**
 * Audio asociado:
 * - Convención deseada: base termina en "__audio"
 * - Compat real visto en bucket: base contiene/termina en "-360-audio"
 *   Ej real: "imagen-de-prueba-loop-360-audio__360.mp3"
 */
export function isAudioAttachmentName(name = "") {
  const base = stripExt(String(name)).toLowerCase();

  // Convención original
  if (base.endsWith("__audio")) return true;

  // Compat: sufijo típico que estás generando
  // Permitimos variantes por si el backend añade tokens extra
  if (base.endsWith("-360-audio__360")) return true;
  if (base.endsWith("-360-audio")) return true;

  // También aceptamos que el token aparezca antes de "__360"
  if (base.includes("-360-audio")) return true;

  return false;
}

/**
 * Clave para agrupar media+audio.
 * - Para audio adjunto quitamos el sufijo de audio.
 * - Mantiene tokens de modo/loop del media (para ligar correctamente).
 */
export function getGroupKeyFromName(name = "") {
  const base = stripExt(String(name));
  const lower = base.toLowerCase();

  // Caso original: __audio al final
  if (lower.endsWith("__audio")) {
    return base.slice(0, -"__audio".length);
  }

  // Caso real: "-360-audio" (a veces con "__360" cerca)
  // Ej: "imagen-de-prueba-loop-360-audio__360" -> "imagen-de-prueba-loop__360"
  // Nota: tu media base suele terminar en "__360", así que lo respetamos.
  if (lower.includes("-360-audio")) {
    // quitamos desde el token "-360-audio" hacia el final
    const idx = lower.lastIndexOf("-360-audio");
    return base.slice(0, idx) + base.slice(idx + "-360-audio".length);
  }

  return base;
}

/**
 * Parse principal para listados: a partir de object.name deduce propiedades.
 */
export function parseObjectName(name = "") {
  const kind = detectKindFromName(name);
  const mode = inferStereoModeFromName(name);
  const loop = inferLoopFromName(name);

  const audioAttachment = kind === "audio" && isAudioAttachmentName(name);
  const groupKey = getGroupKeyFromName(name);

  return {
    name: String(name),
    kind, // "video" | "image" | "audio" | "unknown"
    mode, // "mono" | "3d-tb" | "3d-sbs"
    loop, // boolean
    audioAttachment, // boolean (audio asociado a una experiencia)
    groupKey, // string
  };
}
